import React, { useEffect, useRef, useCallback, useState } from 'react';

let leafletLoading = null;
function injectCss(id, href) {
  if (document.querySelector('#' + id)) return;
  const link = document.createElement('link');
  link.id = id; link.rel = 'stylesheet'; link.href = href;
  document.head.appendChild(link);
}
function loadScript(src, id) {
  return new Promise((resolve) => {
    if (id && document.querySelector('#' + id)) return resolve();
    const s = document.createElement('script');
    if (id) s.id = id;
    s.src = src; s.onload = resolve; s.onerror = resolve;
    document.head.appendChild(s);
  });
}
// Load Leaflet, and (when the caller enables drawing) leaflet-draw too — both from the same CDN the
// main Opportunities map uses.
function loadLeaflet(withDraw) {
  if (window.L && (!withDraw || window.L.Draw)) return Promise.resolve(window.L);
  if (leafletLoading) return leafletLoading.then(async (L) => {
    if (withDraw && L && !L.Draw) {
      injectCss('leaflet-draw-css', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/1.0.4/leaflet.draw.css');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/1.0.4/leaflet.draw.js', 'leaflet-draw-js');
    }
    return L;
  });
  leafletLoading = (async () => {
    injectCss('leaflet-css', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css');
    if (!window.L) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js', 'leaflet-js');
    if (withDraw && window.L && !window.L.Draw) {
      injectCss('leaflet-draw-css', 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/1.0.4/leaflet.draw.css');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/1.0.4/leaflet.draw.js', 'leaflet-draw-js');
    }
    for (let i = 0; i < 60 && !(window.L && window.L.map); i++) await new Promise(r => setTimeout(r, 40));
    return window.L;
  })();
  return leafletLoading;
}

// Keyless OpenStreetMap standard tiles. CARTO's basemap CDN began requiring an API key and stamped
// "API KEY REQUIRED" across every comp map, so it is no longer usable unkeyed. OSM's tile policy
// requires visible attribution (kept below) and no bulk scraping, which this satisfies.
// No {r}/@2x variants exist on this endpoint, so detectRetina must stay OFF or tiles 404.
const STREET_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const SAT_TILES    = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const STREET_ATTR  = '© OpenStreetMap contributors';

// Pin color by comp status (matches the ArvPanel toggle + table pills): active green, pending yellow,
// closed red. Used only when colorByStatus is set; otherwise comps are the historical red.
const STATUS_COLOR = { closed: '#DC2626', active: '#16A34A', pending: '#E0A526' };

function fmtK(n) { return n ? '$' + Math.round(n / 1000) + 'k' : '—'; }
function fmtDate(s) { if (!s) return ''; const d = new Date(s); return isNaN(d) ? s : d.toLocaleDateString(); }

export default function CompMap({ subject, comps, votes = {}, height = 280, numbered = false, colorByStatus = false, enableDraw = false, bound = null, onBoundChange }) {
  const mapDivRef    = useRef(null);
  const mapRef       = useRef(null);
  const tileLayerRef = useRef(null);
  const markersRef   = useRef([]);
  const circlesRef   = useRef([]);
  const boundLayerRef = useRef(null);   // the rendered drawn polygon (from the `bound` prop)
  const drawHandlerRef = useRef(null);  // the active L.Draw.Polygon handler
  const onBoundRef   = useRef(onBoundChange);
  const [tileType, setTileType] = useState('street');
  // True when neither the subject nor any comp has usable coordinates.
  const [noLocation, setNoLocation] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [drawReady, setDrawReady] = useState(false);
  onBoundRef.current = onBoundChange;

  // Initialize map once — explicitly disable geolocation, no locate control, no zoom-to-fit on load
  useEffect(() => {
    let cancelled = false;
    loadLeaflet(enableDraw).then(L => {
      if (cancelled || !mapDivRef.current || mapRef.current || !L) return;
      const map = L.map(mapDivRef.current, {
        zoomControl: true,
        attributionControl: false,
        scrollWheelZoom: false,
        doubleClickZoom: true,
      }).setView(
        // Only centre on the subject when we actually know where it is. Defaulting to downtown Austin
        // put Georgetown/San Marcos/Bastrop subjects on Congress Ave; fitBounds corrects this as soon
        // as any located marker exists, and the overlay below covers the case where none do.
        (Number.isFinite(Number(subject?.lat)) && Number.isFinite(Number(subject?.lng)))
          ? [Number(subject.lat), Number(subject.lng)]
          : [31.0, -97.7],   // neutral Central Texas view, not a claim about this property
        (Number.isFinite(Number(subject?.lat)) && Number.isFinite(Number(subject?.lng))) ? 13 : 7
      );
      tileLayerRef.current = L.tileLayer(STREET_TILES, { attribution: STREET_ATTR, maxZoom: 19 }).addTo(map);
      // Drawing finished → hand the polygon geometry up to the parent (which bounds the comps).
      map.on('draw:created', (e) => {
        const latlngs = e.layer.getLatLngs()[0].map(p => ({ lat: p.lat, lng: p.lng }));
        setDrawing(false);
        drawHandlerRef.current = null;
        if (onBoundRef.current) onBoundRef.current({ type: 'polygon', latlngs });
      });
      mapRef.current = map;
      setDrawReady(!!L.Draw);
      renderMarkers(L);
    });
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switch tile layer
  useEffect(() => {
    const L = window.L;
    if (!L || !mapRef.current) return;
    if (tileLayerRef.current) tileLayerRef.current.remove();
    const opts = tileType === 'satellite'
      ? { attribution: '© Esri World Imagery', maxZoom: 19 }
      : { attribution: STREET_ATTR, maxZoom: 19 };
    tileLayerRef.current = L.tileLayer(
      tileType === 'satellite' ? SAT_TILES : STREET_TILES, opts
    ).addTo(mapRef.current);
  }, [tileType]);

  // Render / clear the drawn boundary polygon from the `bound` prop.
  useEffect(() => {
    const L = window.L;
    if (!L || !mapRef.current) return;
    if (boundLayerRef.current) { boundLayerRef.current.remove(); boundLayerRef.current = null; }
    if (bound && bound.type === 'polygon' && bound.latlngs && bound.latlngs.length >= 3) {
      boundLayerRef.current = L.polygon(bound.latlngs.map(p => [p.lat, p.lng]), {
        color: '#14504A', weight: 2, fillColor: '#14504A', fillOpacity: 0.06, dashArray: '5 5',
      }).addTo(mapRef.current);
    }
  }, [bound]);

  const startDraw = useCallback(() => {
    const L = window.L;
    if (!L || !L.Draw || !mapRef.current) return;
    if (drawHandlerRef.current) { drawHandlerRef.current.disable(); drawHandlerRef.current = null; setDrawing(false); return; }
    const handler = new L.Draw.Polygon(mapRef.current, {
      allowIntersection: false,
      shapeOptions: { color: '#14504A', weight: 2 },
    });
    handler.enable();
    drawHandlerRef.current = handler;
    setDrawing(true);
  }, []);

  const renderMarkers = useCallback((LRef) => {
    const L = LRef || window.L;
    if (!L || !mapRef.current || !subject) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    circlesRef.current.forEach(c => c.remove());
    circlesRef.current = [];

    function pinIcon({ color, size = 16, ring = false, label = '', opacity = 1, label_color = '#fff' }) {
      const cap = size + (ring ? 8 : 4);
      const labelHtml = label
        ? `<span style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:${Math.round(size * 0.6)}px;font-weight:800;color:${label_color};">${label}</span>`
        : '';
      return L.divIcon({
        className: '',
        html:
          `<div style="position:relative;width:${cap}px;height:${cap}px;display:flex;align-items:center;justify-content:center;opacity:${opacity};">` +
            (ring ? `<div style="position:absolute;inset:0;border-radius:50%;background:rgba(255,255,255,0.9);"></div>` : '') +
            `<div style="position:relative;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.4);">` +
              labelHtml +
            `</div>` +
          `</div>`,
        iconSize: [cap, cap], iconAnchor: [cap / 2, cap / 2],
      });
    }

    if (subject.lat && subject.lng) {
      const subMarker = L.marker([subject.lat, subject.lng], {
        icon: pinIcon({ color: '#16A34A', size: 22, ring: true, label: 'S' }),
        zIndexOffset: 1000,
      }).addTo(mapRef.current);
      const tip = `<div style="font-weight:600">${subject.address || 'Subject Property'}</div>` +
        (subject.list_price ? `<div>${fmtK(subject.list_price)}</div>` : '');
      subMarker.bindTooltip(tip, { direction: 'top', offset: [0, -14] });
      markersRef.current.push(subMarker);

      // 0.5mi blue dashed circle
      circlesRef.current.push(L.circle([subject.lat, subject.lng], {
        radius: 805, color: '#3B82F6', weight: 1.5, dashArray: '6 6',
        fillOpacity: 0.04, fillColor: '#3B82F6',
      }).addTo(mapRef.current));
      // 1.0mi gray dashed circle
      circlesRef.current.push(L.circle([subject.lat, subject.lng], {
        radius: 1609, color: '#9C9690', weight: 1, dashArray: '4 6',
        fillOpacity: 0, fillColor: 'transparent',
      }).addTo(mapRef.current));
    }

    // Comp pins — colored by status (when enabled) with the list number as the label; unchecked/excluded
    // closed comps (votes[id] === 'down') render translucent.
    const validComps = (comps || []).filter(c => c.lat && c.lng);
    for (const c of validComps) {
      const vote = votes[c.id];
      const statusColor = colorByStatus ? (STATUS_COLOR[c.comp_status] || STATUS_COLOR.closed) : null;
      let color, opacity;
      if (vote === 'down')      { color = statusColor || '#DC2626'; opacity = 0.35; }
      else if (vote === 'up')   { color = statusColor || '#16A34A'; opacity = 1; }
      else                       { color = statusColor || '#DC2626'; opacity = 1; }
      const label = numbered && c._num != null ? String(c._num) : '';
      const marker = L.marker([c.lat, c.lng], {
        icon: pinIcon({ color, size: label ? 18 : 14, opacity, label }),
      }).addTo(mapRef.current);
      const price = c.price != null ? c.price : c.sold_price;
      const psf = c.price_per_sqft || (price && c.sqft ? Math.round(price / c.sqft) : null);
      const tip = `<div style="font-weight:600">${label ? '#' + label + ' · ' : ''}${c.address || ''}</div>` +
        `<div>${fmtK(price)}${psf ? ' · $' + psf + '/sqft' : ''}</div>` +
        ((c.closed_date || c.close_date) ? `<div style="opacity:.7">Closed ${fmtDate(c.closed_date || c.close_date)}</div>`
          : (c.status ? `<div style="opacity:.7">${c.status}</div>` : ''));
      marker.bindTooltip(tip, { direction: 'top', offset: [0, -10] });
      markersRef.current.push(marker);
    }

    const num = (a, b) => Number.isFinite(Number(a)) && Number.isFinite(Number(b)) && Number(a) !== 0 && Number(b) !== 0;
    const allLatLngs = [];
    if (num(subject.lat, subject.lng)) allLatLngs.push([Number(subject.lat), Number(subject.lng)]);
    validComps.forEach(c => { if (num(c.lat, c.lng)) allLatLngs.push([Number(c.lat), Number(c.lng)]); });
    if (allLatLngs.length >= 2) {
      const bounds = L.latLngBounds(allLatLngs).pad(0.20);
      if (bounds.isValid()) mapRef.current.fitBounds(bounds, { maxZoom: 15 });
    } else if (num(subject.lat, subject.lng)) {
      mapRef.current.setView([Number(subject.lat), Number(subject.lng)], 14);
    }
    // Nothing on this map has a real position — say so instead of showing an unrelated place.
    setNoLocation(allLatLngs.length === 0);
  }, [subject, comps, votes, numbered, colorByStatus]);

  useEffect(() => { renderMarkers(); }, [renderMarkers]);

  return (
    <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--navy-border)' }}>
      <div ref={mapDivRef} style={{ width: '100%', height }} />

      {/* Draw controls (opt-in) */}
      {enableDraw && drawReady && (
        <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 1000, display: 'flex', gap: 6 }}>
          <button onClick={startDraw} title="Draw a polygon to limit the comps to that area" style={{
            padding: '5px 10px', fontSize: 10.5, fontWeight: 700, borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${drawing ? 'var(--teal)' : 'var(--navy-border)'}`,
            background: drawing ? 'var(--teal)' : 'var(--navy-card)', color: drawing ? '#fff' : 'var(--gray-300)',
          }}>✎ {drawing ? 'Click points… (dbl-click to finish)' : 'Draw area'}</button>
          {bound && (
            <button onClick={() => onBoundChange && onBoundChange(null)} title="Remove the boundary" style={{
              padding: '5px 10px', fontSize: 10.5, fontWeight: 700, borderRadius: 6, cursor: 'pointer',
              border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--gray-300)',
            }}>✕ Clear</button>
          )}
        </div>
      )}

      {/* Tile toggle */}
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1000, display: 'flex', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 6, overflow: 'hidden' }}>
        {['street', 'satellite'].map(t => (
          <button key={t} onClick={() => setTileType(t)} style={{
            padding: '4px 10px', fontSize: 10, fontWeight: 600,
            background: tileType === t ? 'var(--teal)' : 'transparent',
            color: tileType === t ? '#fff' : 'var(--gray-300)',
            border: 'none', cursor: 'pointer', textTransform: 'capitalize',
          }}>{t}</button>
        ))}
      </div>

      {/* Legend */}
      {noLocation && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 1200, display: 'grid', placeItems: 'center',
                      background: 'rgba(250,247,242,0.92)', textAlign: 'center', padding: 20 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gray-300)', marginBottom: 6 }}>
              No location data for this property or its comps
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', lineHeight: 1.5, maxWidth: 360 }}>
              The MLS feed did not supply usable coordinates, so these comps were ranked by recency
              rather than distance. The comp table below is still accurate.
            </div>
          </div>
        </div>
      )}
      <div style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 1000, padding: '6px 10px', background: 'rgba(250, 247, 242,0.85)', border: '1px solid var(--navy-border)', borderRadius: 6, fontSize: 9, display: 'flex', gap: 10, alignItems: 'center', color: 'var(--gray-300)' }}>
        <Dot color="#16A34A" />Subject
        {colorByStatus
          ? <><Dot color="#DC2626" />Closed<Dot color="#16A34A" />Active<Dot color="#E0A526" />Pending</>
          : <><Dot color="#DC2626" />Comp<Dot color="#16A34A" />Approved<Dot color="#DC2626" opacity={0.4} />Excluded</>}
      </div>
    </div>
  );
}

function Dot({ color, opacity = 1 }) {
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 3, opacity }} />;
}
